﻿import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { ExportButton } from "@/features/exports/export-button"
import { DataTable, useListParams, type Column } from "@/components/data-table"
import { Button, PageHeader } from "@/components/ui"
import { useOccupations, type Occupation, type OfoOrdering } from "@/features/ofo/api"
import { PAGE_SIZE } from "@/lib/constants"

export const Route = createFileRoute("/ofo/")({ component: OfoScreen })

function OfoScreen() {
  const navigate = useNavigate()
  const { page, setPage, ordering, setOrdering, search, setSearch, debouncedSearch } =
    useListParams<OfoOrdering>("code")
  const query = useOccupations({ page, ordering, q: debouncedSearch })

  const rows = query.data?.items ?? []
  const count = query.data?.count ?? 0
  const searching = debouncedSearch.trim() !== ""

  return (
    <>
      <PageHeader
        eyebrow="Libraries"
        title="OFO register"
        description="The official DHET Organising Framework for Occupations, 2021 edition, with the client's own annotations over it. Search by code or occupation title."
        actions={
          <ExportButton slug="ofo" params={{ q: debouncedSearch, ordering }} />
        }
      />

      {/* The register is far too large to browse, so the result count is the
          feedback that tells a searcher whether to keep typing. */}
      {!query.isLoading && !query.isError && (
        <p className="sr-only" role="status" aria-live="polite">
          {count === 1 ? "1 occupation found" : `${count} occupations found`}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={rows}
        count={count}
        rowKey={(occupation) => occupation.id ?? occupation.code}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        ordering={ordering}
        onOrderingChange={setOrdering}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by code or occupation, for example 2021-681105 or Butcher"
        isLoading={query.isLoading}
        isRefreshing={query.isFetching && !query.isPending}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        onRowClick={(occupation) => {
          if (occupation.id == null) return
          void navigate({
            to: "/ofo/$occupationId",
            params: { occupationId: String(occupation.id) },
          })
        }}
        emptyTitle={searching ? "No occupation matches that" : "The register is empty"}
        emptyDescription={
          searching
            ? "Codes carry the register version, so search 2021-681105 rather than 681105. Part of a title works too."
            : "No occupations have been loaded yet."
        }
        toolbar={
          searching ? (
            <Button variant="secondary" size="sm" onClick={() => setSearch("")}>
              Clear search
            </Button>
          ) : null
        }
      />
    </>
  )
}

// Codes are shown exactly as the register issues them: 2021-681105, never the
// bare six digits, because the prefix is the register version.
const COLUMNS: Column<Occupation>[] = [
  {
    key: "code",
    header: "Code",
    primary: true,
    sortable: true,
    cell: (occupation) => <span className="font-mono">{occupation.code}</span>,
  },
  {
    key: "title",
    header: "Occupation",
    sortable: true,
    cell: (occupation) => <span className="font-medium">{occupation.title}</span>,
  },
  {
    key: "major_group",
    header: "Major group",
    sortable: true,
    cell: (occupation) => (
      <span className="text-muted-foreground">{occupation.major_group}</span>
    ),
  },
  {
    key: "nqf_level",
    header: "NQF level",
    sortable: true,
    cell: (occupation) =>
      occupation.nqf_level ? (
        occupation.nqf_level
      ) : (
        <span className="italic text-muted-foreground">Not set</span>
      ),
  },
]
